import { BannerStatus, Banner } from '../types/banner';
import { processVideoUrls } from './video-url-utils';

const getVideoDownloadStatus = (banner: Banner) => {
  if (!('creativityConfig' in banner)) return null;

  const videos = processVideoUrls(banner.creativityConfig, videoUrl =>
    videoUrl.status ? { status: videoUrl.status } : null
  );

  if (videos.length === 0) return null;

  const pendingCount = videos.filter(v => v.status === 'pending').length;
  const processingCount = videos.filter(v => v.status === 'processing').length;
  const errorCount = videos.filter(v => v.status === 'error').length;
  const readyCount = videos.filter(v => v.status === 'ready').length;

  // Estados de video (solo cuando NO hay banner.status)
  if (errorCount > 0) {
    return {
      label: 'Error',
      message: `Error descargando ${errorCount} video${errorCount > 1 ? 's' : ''}`,
      background: 'linear-gradient(135deg, #ef4444 0%, #dc2626 100%)',
      color: 'white'
    };
  }

  if (processingCount > 0) {
    return {
      label: 'Descargando',
      message: `Descargando videos (${readyCount}/${videos.length})`,
      background: 'linear-gradient(135deg, #f59e0b 0%, #d97706 100%)',
      color: 'white'
    };
  }

  if (pendingCount > 0) {
    return {
      label: 'Analizando',
      message: 'Analizando videos encontrados',
      background: 'linear-gradient(135deg, #64748b 0%, #475569 100%)',
      color: 'white'
    };
  }

  if (readyCount === videos.length) {
    return {
      label: 'Videos listos',
      message: 'Enviando a procesamiento',
      background: 'linear-gradient(135deg, #10b981 0%, #059669 100%)',
      color: 'white'
    };
  }

  return null;
};

export const getBannerStatusData = (banner: Banner) => {
  // PRIORIDAD 1: Si banner tiene status, usar estados de banner
  if (banner.status) {
    const status = banner.status;

    const statusMap: Record<BannerStatus, { label: string; message: string; background: string; color: string }> = {
      PROCESSING_IA: {
        label: 'Analizando',
        message: 'IA analizando el contenido',
        background: 'linear-gradient(135deg, #64748b 0%, #475569 100%)',
        color: 'white'
      },
      PROCESSING_FORM: {
        label: 'Analizando',
        message: 'IA analizando el contenido',
        background: 'linear-gradient(135deg, #64748b 0%, #475569 100%)',
        color: 'white'
      },
      PENDING: {
        label: 'Preparando',
        message: 'Preparando para generación',
        background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
        color: 'white'
      },
      QUEUED: {
        label: 'En cola',
        message: 'Esperando turno de procesamiento',
        background: 'linear-gradient(135deg, #64748b 0%, #475569 100%)',
        color: 'white'
      },
      IN_PROGRESS: {
        label: 'Generando',
        message: 'Creando el asset',
        background: 'linear-gradient(135deg, #f59e0b 0%, #d97706 100%)',
        color: 'white'
      },
      AWAITING_REVIEW: {
        label: 'Revisión',
        message: 'Esperando revisión de contenido',
        background: 'linear-gradient(135deg, #fbbf24 0%, #f59e0b 100%)',
        color: '#78350f'
      },
      DONE: {
        label: 'Listo',
        message: 'Listo para usar',
        background: 'linear-gradient(135deg, #10b981 0%, #059669 100%)',
        color: 'white'
      },
      ERROR: {
        label: 'Error',
        message: 'Error en el procesamiento',
        background: 'linear-gradient(135deg, #ef4444 0%, #dc2626 100%)',
        color: 'white'
      },
      INVALID: {
        label: 'Inválido',
        message: 'Archivo inválido',
        background: 'linear-gradient(135deg, #ef4444 0%, #dc2626 100%)',
        color: 'white'
      },
      FAILED: {
        label: 'Fallido',
        message: 'El procesamiento falló',
        background: 'linear-gradient(135deg, #ef4444 0%, #dc2626 100%)',
        color: 'white'
      }
    };

    return statusMap[status];
  }

  // PRIORIDAD 2: Si NO hay banner.status, mostrar estados de video
  const videoStatus = getVideoDownloadStatus(banner);
  if (videoStatus) {
    return videoStatus;
  }

  // FALLBACK: Estado inicial cuando recién se crea un banner
  return {
    label: 'Enviado',
    message: 'Enviado para procesamiento',
    background: 'linear-gradient(135deg, #e2e8f0 0%, #cbd5e1 100%)',
    color: '#64748b'
  };
};
